import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import toast from 'react-hot-toast'

import APILoadingStatus from 'types/api-loading-status'
import { FavoriteFacility } from 'services/favorite'
import asyncReducers from '../../../utils/async-reducers'
import { addFavorite, getFavorites, removeFavorite } from './favorites.actions'

export type FavoritesState = {
  loading: APILoadingStatus
  facilities: FavoriteFacility[]
  // Set-like collection for fast lookup; synced with `facilities` ids.
  ids: number[]
  // Locations whose mutation is currently in flight — useful for
  // disabling the heart mid-toggle to avoid duplicate dispatches.
  pending: number[]
}

const initialState: FavoritesState = {
  loading: APILoadingStatus.Idle,
  facilities: [],
  ids: [],
  pending: [],
}

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState,
  reducers: {
    clearFavorites(state) {
      state.facilities = []
      state.ids = []
      state.pending = []
    },
    // Optimistic UI: flip the id immediately so the heart paints as
    // soon as the user clicks; reconcile when the request settles.
    optimisticAdd(
      state,
      action: PayloadAction<{ locationId: number; facility?: FavoriteFacility }>
    ) {
      const { locationId, facility } = action.payload
      if (!state.ids.includes(locationId)) {
        state.ids.push(locationId)
      }
      if (
        facility &&
        !state.facilities.some(f => f.id === facility.id)
      ) {
        state.facilities.push(facility)
      }
      if (!state.pending.includes(locationId)) {
        state.pending.push(locationId)
      }
    },
    optimisticRemove(state, action: PayloadAction<{ locationId: number }>) {
      const { locationId } = action.payload
      state.ids = state.ids.filter(id => id !== locationId)
      state.facilities = state.facilities.filter(f => f.id !== locationId)
      if (!state.pending.includes(locationId)) {
        state.pending.push(locationId)
      }
    },
  },
  extraReducers: builder => {
    asyncReducers(
      builder,
      getFavorites,
      'loading',
      (state, action: PayloadAction<FavoriteFacility[]>) => {
        state.facilities = action.payload ?? []
        state.ids = (action.payload ?? []).map(f => f.id)
      }
    )

    // Trust HTTP status: pending → fulfilled means 2xx and the server
    // accepted the request (idempotent on duplicates). On rejection
    // (4xx/5xx/network), the optimistic state is undone and the user
    // is notified.
    builder.addCase(addFavorite.fulfilled, (state, action) => {
      const { locationId } = action.meta.arg
      state.pending = state.pending.filter(id => id !== locationId)
    })
    builder.addCase(addFavorite.rejected, (state, action) => {
      const { locationId } = action.meta.arg
      state.pending = state.pending.filter(id => id !== locationId)
      state.ids = state.ids.filter(id => id !== locationId)
      state.facilities = state.facilities.filter(f => f.id !== locationId)
      toast.error('Could not add to favorites')
    })

    builder.addCase(removeFavorite.fulfilled, (state, action) => {
      const { locationId } = action.meta.arg
      state.pending = state.pending.filter(id => id !== locationId)
    })
    builder.addCase(removeFavorite.rejected, (state, action) => {
      const { locationId } = action.meta.arg
      state.pending = state.pending.filter(id => id !== locationId)
      // We dropped the facility from state on optimisticRemove and
      // can't restore it without the original payload — refetch to
      // get authoritative state.
      toast.error('Could not remove favorite. Refreshing…')
    })
  },
})

export default favoritesSlice.reducer

export const favoriteActions = {
  ...favoritesSlice.actions,
  getFavorites,
  addFavorite,
  removeFavorite,
}
